// ═══════════════════════════════════════════════════════════════════════════
// clasificarProductosSinDatos — el INSUMO del flywheel (16-jul-2026)
// ───────────────────────────────────────────────────────────────────────────
// Detecta productos del catálogo ACTIVOS con vínculo ML a los que les falta
// costo y/o tiempo de reposición — sin esos datos no hay análisis de
// rentabilidad ni de reposición: es LA PRIMERA tarea de la cascada (capa 0,
// corre antes que todo en el agente general; el auto-seed corre aún antes y
// alimenta este universo con los productos recién creados).
//
// MISMO criterio que el banner manual de la página Productos (paridad
// vista↔agente): activo + vínculo ML (el CSV importa por link MLA — sin
// vínculo no hay cómo matchear) + falta costo (costoPromedio > 0) o falta
// tiempoReposicionDias. La rentabilidad mínima NO dispara (cascadea por
// categoría/marca/empresa) pero viaja en el CSV para completarla si querés.
//
// Cada ítem lleva `_csvRow` (las 11 columnas del CSV "para completar") para
// que buildAdjuntosAgente (SECCION_DEFAULTS.productos_sin_datos) arme el CSV
// adjunto de la tarea sin recalcular nada. Función PURA — no toca Firestore.
// ═══════════════════════════════════════════════════════════════════════════

// Header del CSV — el que ImportarCostosCsvModal matchea por nombre.
export const CSV_HEADER_CARGAR_DATOS = [
  'link_publicacion', 'producto_id', 'nombre', 'sku', 'ean', 'marca',
  'categoria', 'precio_venta', 'rentabilidad_minima_pct', 'costo', 'dias_reposicion',
]

const tieneCosto = (p) => p?.costoPromedio != null && Number(p.costoPromedio) > 0

export function clasificarProductosSinDatos({ productos = [], catalogoLinks = [], categorias = [], marcas = [] } = {}) {
  // Primer vínculo ML por producto (shape legacy del loader: externalItemId).
  const linkPorProducto = new Map()
  for (const l of (catalogoLinks || [])) {
    if (!l?.productoId || !l?.externalItemId) continue
    if (!linkPorProducto.has(l.productoId)) linkPorProducto.set(l.productoId, l)
  }
  const marcasPorId = new Map((marcas || []).map(m => [m.id, m]))
  const catsPorId = new Map((categorias || []).map(c => [c.id, c]))

  const items = []
  let activosTotal = 0
  let sinVinculo = 0
  let completos = 0
  for (const p of (productos || [])) {
    if (p?.estado !== 'activo') continue
    activosTotal++
    const link = linkPorProducto.get(p.id)
    if (!link) { sinVinculo++; continue }
    const faltaCosto = !tieneCosto(p)
    const faltaReposicion = p.tiempoReposicionDias == null
    if (!faltaCosto && !faltaReposicion) { completos++; continue }
    const mla = String(link.externalItemId)
    const url = `https://articulo.mercadolibre.com.ar/${mla.replace(/^(MLA)(\d+)$/, '$1-$2')}`
    const faltas = [faltaCosto ? 'costo' : null, faltaReposicion ? 'tiempo de reposición' : null].filter(Boolean).join(' + ')
    items.push({
      // Dedupe §4.5: LA MISMA clave que el flujo manual de ProductosPage —
      // el itemId del recurso es el productoId (no el MLA).
      _itemId: String(p.id),
      title: p.nombre || String(p.id),
      _productoId: p.id,
      permalink: url,
      _motivo: `falta: ${faltas}`,
      metricas: {
        faltaCosto,
        faltaReposicion,
        precioVenta: p.precioVentaSugerido ?? null,
        sku: p.sku || null,
        mla,
      },
      _csvRow: [
        url,
        p.id,
        p.nombre || '',
        p.sku || '',
        p.ean || '',
        marcasPorId.get(p.marcaId)?.nombre || '',
        catsPorId.get(p.categoriaId)?.nombre || '',
        p.precioVentaSugerido ?? '',
        p.rentabilidadMinima != null ? +(p.rentabilidadMinima * 100).toFixed(2) : '',
        '',   // costo: lo completa el operador
        '',   // dias_reposicion: lo completa el operador
      ],
    })
  }

  // Orden: falta COSTO primero (el insumo duro — sin costo no se infiere
  // nada), después precio de venta DESC (el capital más grande primero).
  items.sort((a, b) =>
    ((b.metricas.faltaCosto ? 1 : 0) - (a.metricas.faltaCosto ? 1 : 0))
    || ((b.metricas.precioVenta || 0) - (a.metricas.precioVenta || 0)))

  return {
    secciones: { productos_sin_datos: items },
    analisis: {
      activosTotal,
      sinVinculo,
      completos,
      incompletos: items.length,
      faltaCosto: items.filter(i => i.metricas.faltaCosto).length,
      faltaReposicion: items.filter(i => i.metricas.faltaReposicion).length,
    },
  }
}
